import _ from 'lodash';
import { LineItem as LineItemCore } from '../../core';
import { Weight } from '../../core/product';
import { ProductWeight } from '../../core/product/weight';
import { CustomFields, LineItem, LineItem as LineItemType, ListTemplate, Lot } from '../../types';
import Money from '../../utils/Money';
import { TemplateComponent } from '../DocumentGenerator';
import { applyTableMetadata, formatCurrency, formatDecimal } from '../templates/formatters';
import { LotComponent, LotTemplateType } from './lot.template';

export type ProductsGridOptions = { currency?: string; descriptionTemplate: string | null | undefined };

export type ProductTemplateDataType = ListTemplate<
  Partial<{
    description: string;
    commodity: string;
    variety: string;
    size: string;
    unit: string;
    label: string;
    productCode: string;
    countyOfOrigin: string;
    grade: string;
    organic: string;
    weightReceived?: string;
    lot?: LotTemplateType;
    lots?: LotTemplateType[];
    lotNumbers?: string;
    unitsOrdered: string;
    unitsReceived: string;
    unitsShipped: string;
    weightOrdered: string;
    weightShipped: string;
    orderedPallets: string;
    shippedPallets: string;
    receivedPallets: string;
    buyPrice: string;
    sellPrice: string;
    orderedBuySubtotal: string;
    receivedBuySubtotal: string;
    orderedSellSubtotal: string;
    shippedSellSubtotal: string;
    customFields: CustomFields;
  }>
>;

const shouldUseUnitsShipped = (lineItem: LineItem) => !!lineItem.purchaseOrderId || lineItem.external;

const getLineItemUnitsKey = (lineItem: LineItem, unitType: 'ordered' | 'shipped' | 'received') => {
  if (lineItem.enteredByWeight || lineItem?.parentLineItem?.enteredByWeight) {
    switch (unitType) {
      case 'received':
        return 'weightReceived';
      case 'shipped':
        return shouldUseUnitsShipped(lineItem) ? 'weightShipped' : 'weightPicked';
      case 'ordered':
      default:
        return 'weightOrdered';
    }
  }

  switch (unitType) {
    case 'received':
      return 'unitsReceived';
    case 'shipped':
      return shouldUseUnitsShipped(lineItem) ? 'unitsShipped' : 'unitsPicked';
    case 'ordered':
    default:
      return 'unitsOrdered';
  }
};

const getProductSubtotals = (
  lineItems: LineItem[],
  apTotal: number,
  arTotal: number,
  totalUnitsType: 'ordered' | 'shipped' | 'received'
) => {
  const buyTotal = lineItems.reduce((acc, lineItem) => {
    const buyPrice = lineItem.buyCurrencyPrice ?? lineItem.buyPrice ?? 0;
    const key = getLineItemUnitsKey(lineItem, totalUnitsType);

    return acc + Money.toDollars(buyPrice) * (lineItem[key] ?? 0);
  }, 0);

  const sellTotal = lineItems.reduce((acc, lineItem) => {
    const key = getLineItemUnitsKey(lineItem, totalUnitsType);

    const deliveredPrice = lineItem.deliveredCurrencyPrice ?? lineItem.deliveredPrice;
    const fallbackPrice = lineItem.sellCurrencyPrice ?? lineItem.sellPrice ?? 0;
    const sellPrice = deliveredPrice ?? fallbackPrice;

    return acc + Money.toDollars(sellPrice) * (lineItem[key] ?? 0);
  }, 0);

  return {
    [`${totalUnitsType}BuySubtotal`]: formatCurrency(buyTotal),
    [`${totalUnitsType}SellSubtotal`]: formatCurrency(sellTotal),
    [`${totalUnitsType}ApTotal`]: formatCurrency(buyTotal + apTotal),
    [`${totalUnitsType}ArTotal`]: formatCurrency(sellTotal + arTotal),
  };
};

const calcWeightForGroup = (lineItems: LineItem[], totalUnitsType: 'ordered' | 'shipped' | 'received') =>
  lineItems.reduce((acc, lineItem) => {
    const key = getLineItemUnitsKey(lineItem, totalUnitsType);

    if (lineItem.enteredByWeight || lineItem.parentLineItem?.enteredByWeight) {
      return acc + (lineItem[key] ?? 0);
    }

    if (!lineItem.product) {
      return acc;
    }

    let weight = Weight.of(lineItem.product);

    if (key === 'unitsPicked') {
      weight = weight
        .withLot(lineItem.lot as Lot)
        .withLineItemPalletTags(lineItem.palletTagsOnLineItem ?? []) as ProductWeight;
    }

    if (key === 'unitsReceived') {
      weight = weight.withLineItemVariableWeight(lineItem);
    }

    return acc + weight.forQuantity(lineItem[key] ?? 0);
  }, 0);

const getTotalWeights = (lineItems: LineItem[], totalUnitsType: 'ordered' | 'shipped' | 'received') => {
  return { [`${totalUnitsType}Weight`]: formatDecimal(calcWeightForGroup(lineItems, totalUnitsType)) };
};

const getTotalUnits = (
  lineItems: LineItem[],
  unitsKey: 'unitsOrdered' | 'unitsReceived' | 'unitsShipped',
  totalUnitsType: 'ordered' | 'shipped' | 'received'
) => {
  const totalUnits = lineItems.reduce((acc, lineItem) => {
    const keyForLineItem = unitsKey === 'unitsShipped' && !shouldUseUnitsShipped(lineItem) ? 'unitsPicked' : unitsKey;

    return acc + (lineItem[keyForLineItem] ?? 0);
  }, 0);

  return { [`${totalUnitsType}Units`]: formatDecimal(totalUnits) };
};

const getTotalPallets = (
  lineItems: LineItem[],
  unitsKey: 'unitsOrdered' | 'unitsReceived' | 'unitsShipped',
  totalUnitsType: 'ordered' | 'shipped' | 'received'
) => {
  const groupedByParents = _.groupBy(lineItems, (li) => li.parentLineItemId || li.id);
  const totalPallets = Object.values(groupedByParents).reduce((acc, group) => {
    const totalPalletsForGroup = group.reduce((groupAcc, lineItem) => {
      const cartonsPerPallet = lineItem.product?.cartonsPerPallet;
      const keyForLineItem = unitsKey === 'unitsShipped' && !shouldUseUnitsShipped(lineItem) ? 'unitsPicked' : unitsKey;

      return groupAcc + (cartonsPerPallet ? (lineItem[keyForLineItem] ?? 0) / cartonsPerPallet : 0);
    }, 0);

    return acc + Math.ceil(totalPalletsForGroup);
  }, 0);

  return { [`${totalUnitsType}Pallets`]: formatDecimal(totalPallets) };
};

export const getProductAttributes = (lineItems: LineItem[], apTotal: number, arTotal: number) => {
  return {
    ...getProductSubtotals(lineItems, apTotal, arTotal, 'ordered'),
    ...getProductSubtotals(lineItems, apTotal, arTotal, 'shipped'),
    ...getProductSubtotals(lineItems, apTotal, arTotal, 'received'),
    ...getTotalWeights(lineItems, 'ordered'),
    ...getTotalWeights(lineItems, 'shipped'),
    ...getTotalWeights(lineItems, 'received'),
    ...getTotalUnits(lineItems, 'unitsOrdered', 'ordered'),
    ...getTotalUnits(lineItems, 'unitsShipped', 'shipped'),
    ...getTotalUnits(lineItems, 'unitsReceived', 'received'),
    ...getTotalPallets(lineItems, 'unitsOrdered', 'ordered'),
    ...getTotalPallets(lineItems, 'unitsShipped', 'shipped'),
    ...getTotalPallets(lineItems, 'unitsReceived', 'received'),
  };
};

export const templatizeProductLineItem = (
  lineItem: LineItemType,
  { currency, descriptionTemplate }: ProductsGridOptions
): ProductTemplateDataType['items'][number] => {
  const { product } = lineItem;

  const sellPrice =
    lineItem.deliveredCurrencyPrice ?? lineItem.deliveredPrice ?? lineItem.sellCurrencyPrice ?? lineItem.sellPrice ?? 0;
  const sellPriceFmt = Money.toDollars(sellPrice);
  const buyPrice = Money.toDollars(lineItem.buyCurrencyPrice ?? lineItem.buyPrice ?? 0);

  const unitsShipped = shouldUseUnitsShipped(lineItem) ? lineItem.unitsShipped : lineItem.unitsPicked;
  const weightShipped = shouldUseUnitsShipped(lineItem) ? lineItem.weightShipped : lineItem.weightPicked;

  let lineItemGroupWeightOrdered = 0;
  let lineItemGroupWeightShipped = 0;

  if (lineItem.purchaseOrderId === null && product) {
    lineItemGroupWeightShipped = calcWeightForGroup(lineItem.childrenLineItems ?? [lineItem], 'shipped');
    lineItemGroupWeightOrdered = calcWeightForGroup(lineItem.childrenLineItems ?? [lineItem], 'ordered');
  } else if (lineItem.enteredByWeight ?? lineItem.parentLineItem?.enteredByWeight) {
    lineItemGroupWeightOrdered = lineItem.weightOrdered ?? 0;
    lineItemGroupWeightShipped = lineItem.weightShipped ?? 0;
  } else if (product) {
    const weight = Weight.of(product);

    lineItemGroupWeightOrdered = weight.forQuantity(lineItem.unitsOrdered ?? 0);
    lineItemGroupWeightShipped = weight.forQuantity(unitsShipped ?? 0);
  }

  const shipped = lineItem.enteredByWeight ? weightShipped : unitsShipped;

  const cartons = product?.cartonsPerPallet || 1;
  const shippedPallets = Math.ceil((unitsShipped || 1) / cartons);
  const orderedPallets = Math.ceil((lineItem.unitsOrdered || 1) / cartons);
  const receivedPallets = Math.ceil((lineItem.unitsReceived || 1) / cartons);

  const ordered = lineItem.enteredByWeight ? lineItem.weightOrdered : lineItem.unitsOrdered;
  const received = lineItem.enteredByWeight ? lineItem.weightReceived : lineItem.unitsReceived;

  return {
    description: LineItemCore.productDescription(lineItem, descriptionTemplate),
    commodity: product?.commodity?.name ?? '',
    variety: product?.commodityStyle?.name ?? '',
    size: product?.commoditySize?.name ?? '',
    unit: product?.commodityUnit?.name ?? '',
    label: product?.commodityLabel?.name ?? '',
    productCode: product?.productCode ?? '',
    countyOfOrigin: product?.countryOfOrigin ?? '',
    grade: product?.grade ?? '',
    organic: product?.organic ? 'Organic' : '',
    orderedPallets: formatDecimal(orderedPallets),
    shippedPallets: formatDecimal(shippedPallets),
    receivedPallets: formatDecimal(receivedPallets),
    unitsShipped: formatDecimal(unitsShipped),
    unitsReceived: formatDecimal(lineItem.unitsReceived),
    unitsOrdered: formatDecimal(lineItem.unitsOrdered),
    weightOrdered: formatDecimal(lineItemGroupWeightOrdered),
    weightShipped: formatDecimal(lineItemGroupWeightShipped),
    weightReceived: formatDecimal(lineItem.weightReceived),
    sellPrice: formatCurrency(sellPriceFmt, currency),
    buyPrice: formatCurrency(buyPrice, currency),
    orderedBuySubtotal: formatCurrency(buyPrice * (ordered ?? 0), currency),
    receivedBuySubtotal: formatCurrency(buyPrice * (received ?? 0), currency),
    orderedSellSubtotal: formatCurrency(sellPriceFmt * (ordered ?? 0), currency),
    shippedSellSubtotal: formatCurrency(sellPriceFmt * (shipped ?? 0), currency),
    lot: LotComponent.templatize(lineItem.lot),
    lots: lineItem.lots?.map((lot) => LotComponent.templatize(lot)),
    lotNumbers: lineItem.lots
      ?.map((lot: any) => lot.slug)
      .filter(Boolean)
      .join(', '),
    customFields: product?.customFields,
  };
};

export const ProductsComponent: TemplateComponent<LineItemType[], ProductTemplateDataType, ProductsGridOptions> = {
  templatize: (lineItems, { currency, descriptionTemplate }) => {
    const hasSoldByWeight = lineItems.some((li) => li.enteredByWeight);
    const hasSoldByUnit = lineItems.some((li) => !li.enteredByWeight);

    return {
      hasSoldByWeight,
      hasSoldByUnit,
      ...applyTableMetadata(
        lineItems.map((lineItem) => templatizeProductLineItem(lineItem, { currency, descriptionTemplate }))
      ),
    };
  },
  definitionMap: {
    description: { label: 'Description', type: 'string' },
    commodity: { label: 'Commodity', type: 'string' },
    variety: { label: 'Variety', type: 'string' },
    size: { label: 'Size', type: 'string' },
    unit: { label: 'Unit', type: 'string' },
    label: { label: 'Label', type: 'string' },
    productCode: { label: 'Product Code', type: 'string' },
    countyOfOrigin: { label: 'Country of Origin', type: 'string' },
    grade: { label: 'Grade', type: 'string' },
    organic: { label: 'Organic', type: 'string' },
    lot: { label: 'Lot', type: 'object', children: LotComponent.definitionMap },
    lots: { label: 'Lot', type: 'array', children: LotComponent.definitionMap },
    lotNumbers: { label: 'Lot Numbers', type: 'string' },
    buyPrice: { label: 'Buy Price', type: 'string' },
    sellPrice: { label: 'Sell Price', type: 'string' },
    orderedBuySubtotal: { label: 'Ordered Buy Total', type: 'string' },
    receivedBuySubtotal: { label: 'Received Buy Total', type: 'string' },
    orderedSellSubtotal: { label: 'Ordered Sell Subtotal', type: 'string' },
    shippedSellSubtotal: { label: 'Shipped Sell Subtotal', type: 'string' },
    unitsOrdered: { label: 'Units Ordered', type: 'string' },
    unitsReceived: { label: 'Units Received', type: 'string' },
    unitsShipped: { label: 'Units Shipped', type: 'string' },
    weightOrdered: { label: 'Weight Ordered', type: 'string' },
    weightShipped: { label: 'Weight Shipped', type: 'string' },
    weightReceived: { label: 'Weight Received', type: 'string' },
    orderedPallets: { label: 'Ordered Pallets', type: 'string' },
    shippedPallets: { label: 'Shipped Pallets', type: 'string' },
    receivedPallets: { label: 'Received Pallets', type: 'string' },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'products', section: 'shipment' },
  },
};
